/**
 * The primitives every builder view model is written with: how a locale key is resolved, how a
 * number is printed, what the scheduling unit is called, and what a muscle bar says.
 *
 * Extracted when the sequence tape landed and the builder grew a SECOND canvas. Until then all of
 * this sat in `weekViewModel.ts`, which was accurate while the week was the only thing there was;
 * the tape needs the same number formatting, the same six muscle labels and the same unit noun, and
 * a second copy of any of them would drift on the first change to either.
 *
 * ── Two dependencies, both accepted rather than created ──────────────────────────────────────
 * `t` and `locale` arrive as parameters (`CLAUDE.md`: `model/` imports no i18next). That is also
 * what lets a test hand over a stub that THROWS on a missing key — execution ADR §7 — because an
 * echoing stub makes a missing translation look like a passing test.
 */

import type { MuscleDisplayGroup, MuscleLoad, PlanDraft } from '~/entities/plan-draft';

/** Resolve one locale key, with interpolation. Handed in, never imported. */
export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** Every key this app's builder copy lives under. */
export const KEY = 'web:planBuilder';

export interface MuscleBarView {
  readonly group: MuscleDisplayGroup;
  /** The trainer's word for the group — "Klatka", "Nogi". */
  readonly label: string;
  /** Absolute points, formatted for the locale: `11,5` in Polish, `11.5` in English. */
  readonly points: string;
  /** Whole-number share of its own scope, with the sign: `24%`. */
  readonly percent: string;
  /** Bar width as a percentage of the heaviest group in the same scope, ready for a style. */
  readonly barPercent: number;
}

/**
 * A fixed Monday, used only to ask `Intl` for weekday names.
 *
 * 2024-01-01 was a Monday, so `+ (weekday - 1)` days lands on the right one. This is the mobile
 * app's static PL/EN weekday table replaced by the platform: those tables exist because Hermes
 * throws on `Intl.DateTimeFormat` with a `timeZone`, and browsers do not. Constructed from parts
 * rather than parsed from a string so no time zone is involved at all.
 */
export function weekdayLabels(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  return [0, 1, 2, 3, 4, 5, 6].map((offset) => format.format(new Date(2024, 0, 1 + offset)));
}

/**
 * "Pn" / "Mo" — the two-letter head the 07.09 programator mock draws over each weekday column.
 *
 * LOCALE COPY rather than `Intl`, and that is a measurement rather than a preference: Polish
 * `weekday: 'short'` gives "pon.", "wt.", "śr." — three widths and a full stop — and `'narrow'`
 * gives a single letter that collides three times ("ś" for środa, "s" for sobota, "n" for
 * niedziela). Neither is the mock's fixed two-letter head, so the seven forms are written out.
 * `weekdayLabels` above stays exactly as it is: the FULL name is still the platform's, and it is
 * still what the column says to a screen reader.
 */
export function weekdayShortLabel(t: Translate, weekday: number): string {
  return t(`${KEY}.weekdayShort.${String(weekday)}`);
}

/** `11,5` in Polish, `11.5` in English. Halves are printed; whole numbers carry no decimal. */
export function formatPoints(locale: string, points: number): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(points);
}

export function muscleLabel(t: Translate, group: MuscleDisplayGroup): string {
  // `web:planBuilder.muscle.*`, and deliberately NOT the upstream `exerciseLibrary.muscles.*`
  // group: these are the six DISPLAY buckets, not the backend's fourteen, so four of them have no
  // upstream key at all. Execution ADR §7: "the six muscle-group labels do not borrow from the
  // upstream namespace, because mixing namespaces for one legend is how a re-copy silently
  // changes half of it."
  return t(`${KEY}.muscle.${group}`);
}

export function toBars(t: Translate, locale: string, load: MuscleLoad): MuscleBarView[] {
  return load.entries.map((entry) => ({
    group: entry.group,
    label: muscleLabel(t, entry.group),
    points: formatPoints(locale, entry.points),
    percent: t(`${KEY}.percent`, { value: entry.percent }),
    barPercent: Math.round(entry.barFraction * 100),
  }));
}

/**
 * The unit noun, interpolated everywhere rather than duplicated into a `…Seq` key set.
 *
 * View ADR §2. A cycle on the week GRID is a calendar week and a cycle on the rhythm TAPE is not, so
 * every string that mentions the unit takes it as a variable. Mobile's `…Seq` key twins are copied
 * with the locale files and are never referenced here — they had already drifted beyond the one word
 * they were supposed to differ by.
 */
export function unitNoun(t: Translate, draft: PlanDraft): string {
  return t(`${KEY}.unit.${draft.mode === 'selfpaced' ? 'cycle' : 'week'}`);
}

/**
 * The same noun SPELLED OUT — "tydzień" where `unitNoun` gives "tyg.".
 *
 * Two forms rather than one because the canon uses both and they cannot be the same string: a phase
 * tab reads "tyg. 1–4", where the abbreviation is what keeps three tabs on one band, and the rail's
 * caption reads "Serie / tydzień · udział % w objętości", where an abbreviation in the middle of a
 * sentence reads as a typo. In Polish the two genuinely differ; in English "week" is already short
 * and only the tab abbreviates it.
 */
export function unitNounLong(t: Translate, draft: PlanDraft): string {
  return t(`${KEY}.unitLong.${draft.mode === 'selfpaced' ? 'cycle' : 'week'}`);
}
